import { type LocationFeatureTypes } from '@solinkcorp/api-types';
import { type FeatureStandardDefs } from '@solinkcorp/event-standard';
import { type FeatureTabEntry } from '@solinkcorp/event-standard/lib/src/types';
import memoizeOne from 'memoize-one';
import { HIDE_FROM_DISPLAY_TAG } from 'vd-constants';
import FeaturesUtil from './FeaturesUtil';
import { getAllSitesFeatures } from './LocationUtil';
import { type VideoDiscoverySite } from './types';

export function doesEventTabHaveEventsHiddenFromDisplay(
  tabs: { [key: string]: FeatureTabEntry },
  activeFeature: string,
) {
  // tabs does not include 'all' event tab, check tabs[activeFeature] &&
  if (
    tabs[activeFeature] &&
    'forceInclude' in tabs[activeFeature] &&
    tabs[activeFeature]?.forceInclude?.tags?.eventsQuery?.includes(HIDE_FROM_DISPLAY_TAG)
  ) {
    return true;
  }

  return false;
}

/**
 * Returns a list of features that are enabled by default.
 */
export const getDefaultEnabledFeatures = memoizeOne(
  (featureStandard: {
    tabs: { [feature: string]: { defaultEnabled?: boolean } };
  }): readonly string[] => {
    const defaultEnabledFeatures = Object.keys(featureStandard.tabs).filter(
      (feature) => featureStandard.tabs[feature].defaultEnabled,
    );
    return defaultEnabledFeatures;
  },
);

export const getAuthorizedFeatures = memoizeOne(
  (featureStandard: FeatureStandardDefs): readonly string[] => {
    return Object.keys(featureStandard.tabs).filter((feature) => {
      return FeaturesUtil.canViewFeatureStandardTab(
        featureStandard.tabs[feature].visibilityRestrictions || {},
      );
    });
  },
);

export const getAvailableFeatures = memoizeOne(
  (
    featureStandard: FeatureStandardDefs,
    locations: readonly VideoDiscoverySite[],
  ): {
    availableFeatures: { [feature: string]: LocationFeatureTypes };
    availableFeatureSubtypes: Record<string, boolean>;
  } => {
    const defaultEnabledFeatures = getDefaultEnabledFeatures(featureStandard);
    const authorizedFeatures = getAuthorizedFeatures(featureStandard);
    const allSitesFeatures = getAllSitesFeatures(locations);

    const isFeatureEnabled = (feature: string) =>
      allSitesFeatures?.[feature] || defaultEnabledFeatures.includes(feature);

    const isSubtypeEnabled = (feature: string, subtype: string) =>
      allSitesFeatures?.[feature]?.[subtype] ||
      (defaultEnabledFeatures.includes(feature) &&
        allSitesFeatures?.[feature]?.[subtype] !== false);

    const availableFeatures: { [feature: string]: LocationFeatureTypes } =
      authorizedFeatures.reduce(
        (acc, feature) => {
          if (isFeatureEnabled(feature)) {
            const featureSubtypes = featureStandard.tabs[feature]?.eventSubtypes || {};
            acc[feature] = Object.keys(featureSubtypes).reduce((subtypeAcc, subtype) => {
              if (isSubtypeEnabled(feature, subtype)) {
                subtypeAcc[subtype] = true;
              }
              return subtypeAcc;
            }, {} as LocationFeatureTypes);
          }
          return acc;
        },
        {} as { [feature: string]: LocationFeatureTypes },
      );

    const availableFeatureSubtypes: Record<string, boolean> = Object.values(
      availableFeatures,
    ).reduce((acc, featureTypes) => Object.assign(acc, featureTypes), {});

    return {
      availableFeatures,
      availableFeatureSubtypes,
    };
  },
);
